"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Mail, Phone, MapPin, Loader } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ContactIrForm() {
  const {
    ref: sectionRef,
    isVisible: sectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section ref={sectionRef} className={`py-16 md:py-24 bg-light-background transition-all duration-[var(--duration-slow)] ${sectionVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 max-w-6xl mx-auto">
          {}
          <div className="space-y-8">
            <div>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-primary mb-4">
                Get in Touch
              </h2>
              <p className="text-muted-foreground text-base md:text-lg">
                For investor relations, partnership inquiries, or media requests, reach out to our team.
              </p>
            </div>

            <div className="space-y-6">
              <div className="flex items-start gap-4 group">
                <div className="flex items-center justify-center size-12 rounded-md bg-primary/10 text-primary transition-all duration-[var(--duration-medium)] group-hover:bg-primary group-hover:text-foreground-primary shrink-0">
                  <Phone className="size-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Phone</h3>
                  <p className="text-muted-foreground">+1 (555) 987-6543</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="flex items-center justify-center size-12 rounded-md bg-primary/10 text-primary transition-all duration-[var(--duration-medium)] group-hover:bg-primary group-hover:text-foreground-primary shrink-0">
                  <Mail className="size-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Email</h3>
                  <p className="text-muted-foreground">investor@unitedminingpower.com</p>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="flex items-center justify-center size-12 rounded-md bg-primary/10 text-primary transition-all duration-[var(--duration-medium)] group-hover:bg-primary group-hover:text-foreground-primary shrink-0">
                  <MapPin className="size-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground mb-1">Corporate Headquarters</h3>
                  <p className="text-muted-foreground">123 Mining Plaza, Springfield, USA</p>
                </div>
              </div>
            </div>

            <div className="pt-6 border-t border-border">
              <p className="text-sm text-muted-foreground">
                <strong className="text-foreground">Office Hours:</strong> Mon-Fri: 8am - 6pm ET
              </p>
            </div>
          </div>

          {}
          <div className="bg-background rounded-lg shadow-soft p-6 md:p-8 border border-border">
            <h3 className="text-2xl font-display font-bold text-foreground mb-6">
              Send Us a Message
            </h3>
            
            <FormProvider formId="contact-ir-form-investors-media" sectionName="contact-ir-form" defaultValues={{
            name: "",
            email: "",
            company: "",
            message: ""
          }} validationRules={{
            name: {
              required: true,
              minLength: 2,
              maxLength: 100
            },
            email: {
              required: true,
              pattern: "email"
            },
            company: {
              required: false,
              maxLength: 100
            },
            message: {
              required: true,
              minLength: 10,
              maxLength: 1000
            }
          }} submitText="Send Message" successMessage="Thank you for reaching out. We'll respond within 24 hours." errorMessage="Failed to submit. Please try again or contact us directly.">
              <ContactFormInner />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-6 bg-accent/10 border border-accent rounded-md">
        <p className="text-foreground font-medium mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="w-full">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <FormField control={control} name="name" rules={{
        required: "Name is required",
        minLength: {
          value: 2,
          message: "Name must be at least 2 characters"
        },
        maxLength: {
          value: 100,
          message: "Name must not exceed 100 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" {...field} className="transition-all duration-[var(--duration-fast)] focus:border-accent" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" {...field} className="transition-all duration-[var(--duration-fast)] focus:border-accent" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="company" rules={{
        maxLength: {
          value: 100,
          message: "Company name must not exceed 100 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Company (Optional)</FormLabel>
              <FormControl>
                <Input placeholder="Your Company" {...field} className="transition-all duration-[var(--duration-fast)] focus:border-accent" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="message" rules={{
        required: "Message is required",
        minLength: {
          value: 10,
          message: "Message must be at least 10 characters"
        },
        maxLength: {
          value: 1000,
          message: "Message must not exceed 1000 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel>Message</FormLabel>
              <FormControl>
                <Textarea placeholder="Tell us about your inquiry..." {...field} className="min-h-32 transition-all duration-[var(--duration-fast)] focus:border-accent resize-none" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-red-800 text-sm">{submitError}</p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full transition-all duration-[var(--duration-medium)] hover:shadow-medium">
          {isSubmitting ? <>
              <Loader className="animate-spin size-4 mr-2" />
              Sending...
            </> : "Send Message"}
        </Button>
      </div>
    </Form>;
}